/**
 * Share a gilding. The shareable surface is the public piece page
 * `/remix/:id` — it shows the image and invites the viewer to remix it with
 * their own character, which is exactly the "easy access for others" the share
 * link is for.
 *
 * Uses the native share sheet (Web Share API) when the device offers one — that
 * gives the real OS share card on mobile — and falls back to copying the link
 * to the clipboard everywhere else. A user-cancelled native sheet is NOT a
 * failure; it resolves as 'shared' so callers don't flash an error.
 */
export type ShareOutcome = 'shared' | 'copied' | 'failed';

export function gildingShareUrl(id: string): string {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://gilding.app';
  return `${origin}/remix/${id}`;
}

export async function shareGilding(id: string): Promise<ShareOutcome> {
  const url = gildingShareUrl(id);
  const data: ShareData = {
    title: 'A gilding on Gilding',
    text: 'See the prompt behind this, and remix it with your own character.',
    url,
  };

  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  if (nav?.share && (!nav.canShare || nav.canShare(data))) {
    try {
      await nav.share(data);
      return 'shared';
    } catch (e) {
      // The user dismissing the sheet throws AbortError — treat as a no-op,
      // not a failure, and don't fall through to a surprise clipboard write.
      if ((e as Error)?.name === 'AbortError') return 'shared';
      // Any other native-share failure: fall back to copying the link.
    }
  }

  try {
    await nav!.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
